import { useImperativeHandle, useLayoutEffect, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { NoteEditorProps } from './noteEditorTypes';
import styles from './NoteCard.module.css';

/** The text box shows at least this many lines. */
export const NOTE_MIN_ROWS = 4;

/**
 * The plain text box of the note: used while the CodeMirror editor chunk loads and when it fails to
 * load. It grows with its content (no inner scrollbar).
 */
export function NoteTextarea({
  ref,
  value,
  placeholder,
  ariaLabel,
  maxLength,
  onChange,
  onCaret,
  onSave,
  onEscape,
}: NoteEditorProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const [fieldWidth, setFieldWidth] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      focus(caret) {
        const element = field.current;
        if (element === null) return;
        element.focus({ preventScroll: true });
        const offset = Math.min(caret ?? element.value.length, element.value.length);
        element.setSelectionRange(offset, offset);
      },
      blur() {
        field.current?.blur();
      },
    }),
    [],
  );

  // A narrower or wider box wraps the text differently: re-measure when its width changes.
  useEffect(() => {
    const element = field.current;
    if (element === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next !== undefined) setFieldWidth(Math.round(next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The box grows with its text (never an inner scrollbar): measure the content height with the
  // flex stretch switched off, then fix the height.
  useLayoutEffect(() => {
    const element = field.current;
    if (element === null) return;
    element.style.flex = 'none';
    element.style.height = 'auto';
    const chrome = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + chrome}px`;
    element.style.flex = '';
  }, [value, fieldWidth]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Esc or Enter that confirms or cancels an IME candidate belongs to the IME, not to the note.
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
    }
  };

  return (
    <textarea
      ref={field}
      className={styles.textarea}
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      aria-label={ariaLabel}
      rows={NOTE_MIN_ROWS}
      onChange={(event) => onChange(event.target.value)}
      onSelect={(event) => onCaret?.(event.currentTarget.selectionStart)}
      onKeyDown={onKeyDown}
    />
  );
}
